// Shared "List for sale" dialog — the single source of truth for BOTH the
// certificate listing (Story 5.2, ListForSaleDialog) and the sealed-dossier
// listing (Story 5.3, ListSealedDossierForSaleDialog). Those two flows are
// identical apart from a handful of parameters (item noun, min price, floor
// scope, the sealed-dossier scarcity note) and which hooks back the data, so
// the form + fee panel + expiry + anonymity + KYC banner + all styling live
// here ONCE. Change the look or the copy here and both surfaces update.
//
// The two wrappers own the React-Query hooks (a floor-price query and a
// create-listing mutation each have different arg/return shapes, and hooks
// can't be chosen at runtime), then inject their results + callbacks as props.
//
// Outcome handling (shared):
//   * success → onListed(listing_id) [undo toast + invalidation] then close
//   * 402 KYC_REQUIRED → in-dialog KYC banner (Story 5.8); dialog STAYS OPEN
//     per UX-DR L1238 so the draft survives any verification tab
//   * 409 LISTING_ALREADY_EXISTS → toast + close + onAlreadyExists() [invalidate]
//   * 400/500 → in-dialog error + toast; dialog stays open

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Switch,
} from "@greenhold/components";
import { useEffect, useState, type ReactNode } from "react";
import toast from "react-hot-toast";

import { useInitiateKyc } from "../account/useInitiateKyc";
import {
  computeFeeBreakdown,
  eurosStringToCents,
  formatCents,
} from "./feeMath";

/** The wire still carries an expiry field; listings never expire now (manual
 *  removal only), so we always send `null` ("Never"). */
export type ListingExpiresInDays = 7 | 30 | 90 | null;

interface KycRequiredDetail {
  threshold_cents: number;
  current_cumulative_cents: number;
  attempted_price_cents: number;
  single_tx_threshold_cents?: number;
}

export interface ListingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** "create" (default) lists a new item; "edit" changes an existing active
   *  listing's price / allow_bidding. Edit mode seeds the form from
   *  `initialPriceCents` / `initialAllowBidding`, re-labels the title +
   *  submit button, and its `createListing` prop is the edit mutation. */
  mode?: "create" | "edit";
  /** Edit mode — the listing's current asking price (seeds the input). */
  initialPriceCents?: number;
  /** Edit mode — the listing's current allow-bidding flag (seeds the switch). */
  initialAllowBidding?: boolean;

  // ── Copy / config that differs between the two surfaces ──
  /** e.g. "certificate" | "sealed dossier" — fills the title. */
  itemNoun: string;
  /** DOM id prefix so the two dialogs never collide (e.g. "list" | "list-sd"). */
  idPrefix: string;
  /** Minimum listing price in cents (share €0.10 = 10; dossier €1.00 = 100). */
  minCents: number;
  /** What the floor price is scoped to, e.g. "creator + tier" | "drop". */
  floorScopeLabel: string;
  /** Optional calm/factual context note (the sealed-dossier scarcity line). */
  scarcityNote?: ReactNode;
  /** Message shown when the item already has an active listing (409). */
  alreadyExistsMessage: string;

  // ── Data + actions the wrapper injects from its own hooks ──
  /** Floor price for autopop; null = none, undefined = still loading. */
  floorPriceCents: number | null | undefined;
  createListing: (args: {
    price_cents: number;
    expires_in_days: ListingExpiresInDays;
    seller_anonymous: boolean;
    allow_bidding: boolean;
  }) => Promise<{ listing_id: string }>;
  isSubmitting: boolean;
  /** Success — do the undo toast + query invalidation. The dialog then closes. */
  onListed: (listingId: string) => void;
  /** 409 already-listed — do the query invalidation (dialog toasts + closes). */
  onAlreadyExists: () => void;
}

export function ListingDialog({
  open,
  onOpenChange,
  mode = "create",
  initialPriceCents,
  initialAllowBidding,
  itemNoun,
  idPrefix,
  minCents,
  floorScopeLabel,
  scarcityNote,
  alreadyExistsMessage,
  floorPriceCents,
  createListing,
  isSubmitting,
  onListed,
  onAlreadyExists,
}: ListingDialogProps) {
  const isEdit = mode === "edit";
  const [priceEuros, setPriceEuros] = useState("");
  const [allowBidding, setAllowBidding] = useState(false);
  const [kycRequired, setKycRequired] = useState<KycRequiredDetail | null>(null);
  const [genericError, setGenericError] = useState<string | null>(null);

  const kycInitiateMutation = useInitiateKyc();

  const priceInputId = `${idPrefix}-price-input`;
  const priceHelpId = `${idPrefix}-price-help`;
  const allowBiddingId = `${idPrefix}-allow-bidding`;
  const minLabel = formatCents(minCents);

  // Edit mode — seed the form from the listing's current values when the
  // dialog opens (takes precedence over floor autopop below).
  useEffect(() => {
    if (open && isEdit) {
      if (initialPriceCents != null) {
        setPriceEuros((initialPriceCents / 100).toFixed(2));
      }
      setAllowBidding(initialAllowBidding ?? false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit]);

  // Autopop the price input when the floor price loads (create only, and only
  // if the user hasn't already typed something).
  useEffect(() => {
    if (!isEdit && open && priceEuros === "" && floorPriceCents != null) {
      setPriceEuros((floorPriceCents / 100).toFixed(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPriceCents, open]);

  // Reset state when the dialog closes.
  useEffect(() => {
    if (!open) {
      setPriceEuros("");
      setAllowBidding(false);
      setKycRequired(null);
      setGenericError(null);
    }
  }, [open]);

  const priceCents = eurosStringToCents(priceEuros);
  const priceCentsValid =
    Number.isFinite(priceCents) && priceCents >= minCents;
  const feeBreakdown = priceCentsValid
    ? computeFeeBreakdown(priceCents)
    : { platformFeeCents: 0, creatorRoyaltyCents: 0, estimatedProceedsCents: 0 };

  const submitDisabled = !priceCentsValid || isSubmitting;

  async function handleSubmit() {
    setKycRequired(null);
    setGenericError(null);
    try {
      const result = await createListing({
        price_cents: priceCents,
        // Listings never expire (manual removal only) and are always
        // anonymous — both are fixed platform policy now, not user choices.
        expires_in_days: null,
        seller_anonymous: true,
        allow_bidding: allowBidding,
      });
      onListed(result.listing_id);
      onOpenChange(false);
    } catch (err) {
      const detail = err as {
        code?: string;
        message?: string;
        status?: number;
        threshold_cents?: number;
        current_cumulative_cents?: number;
        attempted_price_cents?: number;
        single_tx_threshold_cents?: number;
      };
      if (detail?.code === "KYC_REQUIRED") {
        // Story 5.8 — surface the KYC banner. Dialog STAYS OPEN per
        // UX-DR L1238 so the form state survives the redirect tab.
        setKycRequired({
          threshold_cents: detail.threshold_cents ?? 10_000,
          current_cumulative_cents: detail.current_cumulative_cents ?? 0,
          attempted_price_cents: detail.attempted_price_cents ?? priceCents,
          single_tx_threshold_cents: detail.single_tx_threshold_cents,
        });
        return;
      }
      if (detail?.code === "LISTING_ALREADY_EXISTS") {
        toast.error(alreadyExistsMessage);
        onOpenChange(false);
        onAlreadyExists();
        return;
      }
      setGenericError(detail?.message ?? "Listing failed. Try again.");
      toast.error(detail?.message ?? "Listing failed. Try again.");
    }
  }

  async function handleVerifyIdentity() {
    try {
      const outcome = await kycInitiateMutation.mutateAsync();
      if (outcome.onboarding_url) {
        // Provider-hosted verification exists — open it in a NEW TAB. The
        // original tab (with this dialog) stays mounted so the listing
        // draft survives the round-trip per UX-DR L1238 state-preservation.
        if (typeof window !== "undefined") {
          window.open(outcome.onboarding_url, "_blank", "noopener");
        }
        return;
      }
      // No hosted step (beta mock provider handles verification
      // server-side) — reflect that instead of erroring.
      toast.success("Verification is handled for you. Try listing again.");
      setKycRequired(null);
    } catch (err) {
      const detail = err as { code?: string; message?: string; tier?: string };
      if (detail?.code === "ALREADY_KYC_VERIFIED") {
        toast.success("You're already verified. Try listing again.");
        setKycRequired(null);
        return;
      }
      toast.error(detail?.message ?? "Could not start verification. Try again.");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[85vh] max-w-md gap-0 overflow-y-auto overflow-x-hidden border border-brand-gold-500/40 bg-brand-parchment p-0 text-brand-ink-900 shadow-xl sm:rounded-xl">
        <AlertDialogHeader className="space-y-1.5 border-b border-brand-gold-500/40 bg-brand-parchment-hi px-6 pb-5 pt-6 text-left">
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-brand-forest-700">
            Greenhold · Marketplace
          </p>
          <AlertDialogTitle className="font-heading text-2xl font-semibold leading-tight tracking-tight text-brand-ink-900">
            {isEdit
              ? `Edit your ${itemNoun} listing`
              : `List this ${itemNoun} for sale`}
          </AlertDialogTitle>
          <AlertDialogDescription className="font-body text-sm text-brand-ink-700">
            {isEdit
              ? "Change your asking price or bidding preference. The update goes live immediately — a buyer who purchases before you save keeps the item at the old price."
              : "Set your asking price. You keep 85% after the buyer pays — Greenhold's 10% platform fee (VAT incl.) and the creator's 5% royalty come out of the sale."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-5 px-6 pb-6 pt-6">
          {/* Asking price */}
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Label
                htmlFor={priceInputId}
                className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700"
              >
                Asking price
              </Label>
              <span
                className="h-px flex-1 bg-brand-gold-500/40"
                aria-hidden="true"
              />
            </div>
            <div className="relative">
              <span
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 font-mono text-lg font-semibold text-brand-forest-700"
                aria-hidden="true"
              >
                €
              </span>
              <Input
                id={priceInputId}
                type="number"
                inputMode="decimal"
                min={minCents / 100}
                step={0.01}
                value={priceEuros}
                onChange={(e) => setPriceEuros(e.target.value)}
                placeholder={
                  floorPriceCents != null
                    ? (floorPriceCents / 100).toFixed(2)
                    : (minCents / 100).toFixed(2)
                }
                disabled={isSubmitting}
                aria-describedby={priceHelpId}
                className="h-12 rounded-lg border-brand-gold-500/50 bg-brand-paper-50 pl-9 pr-4 font-mono text-lg font-semibold tabular-nums text-brand-ink-900 placeholder:font-normal placeholder:text-brand-ink-700/70 focus-visible:border-brand-forest-500 focus-visible:ring-brand-forest-500/30"
              />
            </div>
            <p
              id={priceHelpId}
              className="font-mono text-[11px] tracking-wide text-brand-forest-700"
            >
              Minimum {minLabel}.{" "}
              {floorPriceCents != null
                ? `Current floor for this ${floorScopeLabel}: ${formatCents(
                    floorPriceCents,
                  )}.`
                : `No other active listings for this ${floorScopeLabel} yet.`}
            </p>
          </div>

          {/* Optional calm/factual context (sealed-dossier scarcity note). */}
          {scarcityNote && (
            <p className="font-body text-sm text-brand-ink-700">
              {scarcityNote}
            </p>
          )}

          {/* Fee breakdown */}
          <div className="rounded-lg border border-brand-gold-500/40 bg-brand-parchment-hi p-4">
            <div className="mb-3 flex items-center gap-3">
              <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-brand-forest-700">
                Fee breakdown
              </span>
              <span
                className="h-px flex-1 bg-brand-gold-500/40"
                aria-hidden="true"
              />
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-brand-ink-700">
                  Platform fee (10%, VAT incl.)
                </dt>
                <dd
                  data-testid="fb-platform"
                  className="font-mono tabular-nums text-brand-ink-900"
                >
                  {formatCents(feeBreakdown.platformFeeCents)}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-brand-ink-700">
                  Creator royalty (5%, paid direct)
                </dt>
                <dd
                  data-testid="fb-creator"
                  className="font-mono tabular-nums text-brand-ink-900"
                >
                  {formatCents(feeBreakdown.creatorRoyaltyCents)}
                </dd>
              </div>
              <div className="mt-1 flex items-baseline justify-between border-t border-brand-gold-500/40 pt-2">
                <dt className="font-semibold text-brand-ink-900">
                  You receive (85%)
                </dt>
                <dd
                  data-testid="fb-proceeds"
                  className="font-mono text-base font-bold tabular-nums text-brand-forest-700"
                >
                  {formatCents(feeBreakdown.estimatedProceedsCents)}
                </dd>
              </div>
            </dl>
          </div>

          {/* Allow bidding — seller opts into offers below the asking price.
              (Listings are always anonymous + never expire, so those are no
              longer choices shown here.) */}
          <div className="flex items-center justify-between gap-4 rounded-lg border border-brand-gold-500/40 bg-brand-parchment-hi px-4 py-3">
            <div className="space-y-0.5">
              <Label
                htmlFor={allowBiddingId}
                className="font-body text-sm font-semibold text-brand-ink-900"
              >
                Allow bidding
              </Label>
              <p className="font-body text-xs text-brand-ink-700">
                Let buyers offer below your asking price.
              </p>
            </div>
            <Switch
              id={allowBiddingId}
              checked={allowBidding}
              onCheckedChange={setAllowBidding}
              disabled={isSubmitting}
            />
          </div>

          {kycRequired && (
            <div
              role="status"
              data-testid="kyc-required-banner"
              className="space-y-2 rounded-lg border border-state-warning-fill/50 bg-brand-paper-50 p-3"
            >
              <p className="font-body text-sm text-brand-ink-900">
                A quick verification step is needed before you can sell at this
                volume.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  data-testid="kyc-verify-identity-button"
                  disabled={kycInitiateMutation.isPending}
                  onClick={() => {
                    void handleVerifyIdentity();
                  }}
                >
                  {kycInitiateMutation.isPending
                    ? "Opening verification…"
                    : "Verify identity"}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid="kyc-lower-price-button"
                  onClick={() => setKycRequired(null)}
                >
                  Lower price
                </Button>
              </div>
            </div>
          )}
          {genericError && (
            <p
              role="alert"
              className="flex items-center gap-2 rounded-md border border-state-error-fill/30 bg-brand-paper-50 px-3 py-2 font-mono text-xs font-semibold text-state-error-fill"
            >
              <span aria-hidden="true">⚠</span>
              {genericError}
            </p>
          )}
        </div>

        <AlertDialogFooter className="gap-2 border-t border-brand-gold-500/40 bg-brand-parchment-hi px-6 py-4">
          <AlertDialogCancel
            disabled={isSubmitting}
            className="rounded-lg font-mono text-xs font-semibold uppercase tracking-wide"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={submitDisabled}
            onClick={(e) => {
              // Prevent the dialog's default close-on-action so the mutation
              // owns close timing (it keeps the dialog open on 402
              // KYC_REQUIRED for the in-dialog alert).
              e.preventDefault();
              void handleSubmit();
            }}
            className="rounded-lg font-mono text-xs font-semibold uppercase tracking-[0.14em] shadow-md hover:shadow-lg"
          >
            {isEdit
              ? isSubmitting
                ? "Saving…"
                : "Save changes"
              : isSubmitting
                ? "Listing…"
                : "List for sale"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
